"use client";

/**
 * One quiet Pro line after the findings. It replaces the two blurred
 * teaser cards a free account used to see inside a result (the seller
 * activity with mock figures, the negotiation brief): the locked data
 * stays hidden, nothing is blurred, one sentence names what Pro adds
 * and one link leads there (UI critique, 2026-10-18). The link's label
 * carries the viewer's state, like ProCta: the free trial for accounts
 * that can start one, the win-back after an ended preview, the plain
 * upgrade otherwise. A locked card can say its own sentence the same way
 * (``what``): the same home elsewhere does, in place of a full-width
 * button (owner, 2026-10-22). Never drawn for a Pro or trial account: they
 * have what it offers (owner decision 5, 2026-10-29).
 */
import LocalizedLink from "@/lib/localized-link";
import { useT } from "@/lib/locale-context";
import { useAuth } from "@/lib/auth-context";
import { hasProFeatures } from "@/lib/pro";

export default function ProLine({ seller = false, negotiation = false, what, previewEnded, className = "px-1" }: {
  /** Seller activity (price history, days on market) is locked here. */
  seller?: boolean;
  /** The negotiation position is locked here. */
  negotiation?: boolean;
  /** The sentence itself, for a locked card that names its own data. */
  what?: string;
  previewEnded?: boolean;
  className?: string;
}) {
  const t = useT();
  const { user } = useAuth();
  if (!what && !seller && !negotiation) return null;
  if (hasProFeatures(user)) return null;
  const sentence = what ?? (seller && negotiation
    ? t("result.pro_line.both")
    : seller
      ? t("result.pro_line.seller")
      : t("result.pro_line.negotiation"));
  const cta = previewEnded
    ? t("pricing.trial_ended.cta")
    : !user || user.pro_trial_eligible === true
      ? t("map.personal.trial_cta")
      : t("result.market.pro_cta");
  return (
    <p className={`${className} text-[13px] leading-relaxed text-gray-600`}>
      {sentence}{" "}
      {/* The link wraps as one piece, never "7 gün" on one line and the rest on the next. */}
      <LocalizedLink href="/pricing" className="whitespace-nowrap font-semibold text-gray-900 underline-offset-2 underline decoration-gray-300 hover:decoration-gray-900">
        {cta}
      </LocalizedLink>
    </p>
  );
}
